import { type Client, type HttpClient, type RouteGenerator, View } from "silver";
import { type Book } from "core/Data/Book";
import { type Modal } from "shared/Modal";
import { type MyonApiResponse, isMyonApiSuccess } from "shared/Api/MyonApiResponse";
import { type MyonEnv, isEnvUserAuthorized } from "shared/Api/MyonEnv";
import { type MyonLanguageLoader } from "Lang";


type LaunchQuizOptions = {
	target?: string;
	source?: HTMLElement;
	projectId?: number;
};

async function showDemoTooltipModal(title: string, body: string, confirm: string, modal: Modal, source?: HTMLElement): Promise<void> {
	const html = `
		<div class="announcement -demo -modal" style="width:400px">
			<i class="announcement_icon" role="presentation"></i>
			<h2 class="announcement_title">{{ title }}</h2>
			<p class="announcement_body">{{ body }}</p>
			<div class="announcement_actions">
				<button type="button" class="button -primary" data-myon-click="handleDismiss">
					{{ confirm }}
				</button>
			</div>
		</div>`;
	const view = new View<{ title: string, body: string, confirm: string }>(html, {
		handleDismiss: () => modal.back(null)
	}, { title, body, confirm });
	await modal.show(view.element, { source });
}

export class BookARQuizService {

	private isEligibleByUserIdAndProjectId: { [ userId: string ]: { [ projectId: string ]: Promise<boolean> } } = { };

	constructor(
		private client: Client,
		private httpClient: HttpClient,
		private router: RouteGenerator,
		private env: MyonEnv,
		private modal: Modal,
		private langLoader: MyonLanguageLoader
	) { }

	public async isUserEligible(userId: number, projectId?: number): Promise<boolean> {
		if (! this.isEligibleByUserIdAndProjectId.hasOwnProperty(userId)) {
			this.isEligibleByUserIdAndProjectId[ userId ] = { };
		}
		if (this.isEligibleByUserIdAndProjectId[ userId ].hasOwnProperty(String(projectId))) {
			return this.isEligibleByUserIdAndProjectId[ userId ][ String(projectId) ];
		}
		const params: any = { user_id: userId };
		if (projectId) {
			params.project_id = projectId;
		}
		const url = this.router.generate("api\\acceleratedReader", "isUserEligible", params, "json");
		return this.isEligibleByUserIdAndProjectId[ userId ][ String(projectId)] = this.httpClient.get(url)
			.then((response: MyonApiResponse<boolean>) => {
				if (!isMyonApiSuccess(response)) {
					console.error(response);
					throw new Error("Could not determine user eligibility");
				}
				return response.result;
			});
	}

	public isBookEligible(book: Book): boolean {
		return !!book.arQuiz;
	}

	public async isBookAndUserligible(book: Book, userId: number, projectId?: number): Promise<boolean> {
		if (!this.isBookEligible(book)) {
			return false;
		}
		return this.isUserEligible(userId, projectId);
	}

	public openArQuiz(bookId: number, options: LaunchQuizOptions = { }): void {
		if (isEnvUserAuthorized(this.env) && this.env.auth.isDemoStudent) {
			this.showDemoQuiz(options.source).catch(console.error);
			return;
		}
		const url = this.getArQuizLink(bookId, options.projectId);
		this.client.open(url, { target: options.target });
	}

	private async showDemoQuiz(source?: HTMLElement): Promise<void> {
		const lang = await this.langLoader.load("Pages\\Admin\\DemoStudent");
		return showDemoTooltipModal(
			lang.TITLE_AR_QUIZ_TOOLTIP(),
			lang.DESCR_AR_QUIZ_TOOLTIP(),
			lang.ACTION_GOT_IT(),
			this.modal,
			source
		);
	}

	private getArQuizLink(bookId: number, projectId?: number) {
		const params: any = { book_id: bookId };
		if (projectId) {
			params.project_id = projectId;
		}
		return this.router.generate("api\\acceleratedReader", "index", params, "html");
	}
}
